import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { initState, setFilter, setOption } from "../../actions/overview";
import OverviewDisplay from "./OverviewDisplay";

// determine if we want to abstract useNavigate as NIBHV doesnt have newest react-router-dom
// import {useNavigate} from "react-router-dom";

// Should be provided using default fetcher in app enclosing <SWRConfig/>
export const fetcher = (url) => axios.get(url).then((res) => res.data);
export const optionsFetcher = (url) => axios.options(url).then((res) => res.data);

// Default state for an Overview
const defaultState = {
    identifier: false,
    bulkPreselectAll: false,
    bulkEnabled: false,
    title: false,
    bulk: {},
    bulkData: {},
    filter: {},
    ordering: {
        columnIdentifier: "",
        direction: ""
    },
    type: "cursorPaginator",
    displayFake: false,
    pagination: {
        cursorPaginator: {
            offset: 0,
            size: 20
        },
        indexPaginator: {
            page: 1,
            size: 20,
            sets: 1 // the amount of sets (pages) to load (or have been loaded)
        },
        cursorInfinite: {
            offset: 0,
            size: 20,
            loadAllAllowed: false
        },
        indexInfinite: {
            page: 1,
            size: 20,
            sets: 1, // the amount of sets (pages) to load (or have been loaded)
            loadAllAllowed: false
        }
    },
    // runtime settings
    size: 20,
    offset: 0,
    sets: 1
};

export const overviewDefaultState = defaultState;

// This is where the magic happens. It handles all logic for the Overview component
function Overview(props, ref) {
    // create a ref for accurate (real) dom access (like scrolling when filter is reset)
    const titleRef = useRef();

    // create an overviewRef which can be passed back to the parent so we can trigger a mutate
    const overviewRef = useRef();

    // creates a custom handle for this component which can handle the passed ref (in this components props) and assign something to it
    useImperativeHandle(ref, () => ({
        // We expose a mutate funciton, which triggers the mutate on the overview. This is useful when a parent needs to trigger an update but nothing more
        mutate: () => overviewRef.current.mutate(),
        resetFilters: () => resetFilters(),
        revalidate: () => {
            // console.log("revalidate", props.contextData);
        }
    }));

    // determine if a different ref is passed for scrolling (like scrolling within a div instead of window)
    const scrollRef = props?.scrollInsideRef ? props.scrollInsideRef : titleRef;

    const dispatch = useDispatch();

    // const navigate = useNavigate(); // NIBHV react-router-dom is older, work around used in Display

    const stateIdentifier = {
        // A (serializable) json object which will be used to identify the (instance) state
        identifier: props.identifier,
        ...props?.identKey
    };

    const classNamePrefix = props?.classNamePrefix ? props.classNamePrefix : "overview-redux";

    // (de)serialization only happens when entering state (reducer) and retrieving the state (here)
    let state = useSelector((state) => state.OverviewState?.[JSON.stringify(stateIdentifier)]);

    // Initialize a state for this instance of Overview but only when identifier has changed
    useEffect(() => {
        if (!state) {
            if (!props?.identifier) {
                console.log("No identifier set!");
            }
            const type = Object.keys(defaultState.pagination).includes(props?.type) ? props.type : defaultState.type;

            // We overlay objects to create a final initial state
            let localInitialState = Object.assign(
                {},
                defaultState, // low-level defaults
                {
                    identifier: props.identifier, // overlay the identifier (NOT the stateIdentifier!)
                    identKey: props?.identKey, // additional vital information required for querying
                    type: type,
                    displayFake: props?.definition?.displayFake || false,
                    displaySummary: props?.definition?.displaySummary || false
                },
                props.definition?.defaultOrderIdentifier
                    ? {
                          ordering: {
                              ...defaultState.ordering,
                              columnIdentifier: props.definition.defaultOrderIdentifier
                          }
                      }
                    : {},
                {
                    pagination: {
                        ...defaultState.pagination,
                        [type]: {
                            ...defaultState.pagination[type],
                            loadAllAllowed: props.definition.loadAllAllowed,
                            size: props?.definition?.pagination?.[type]?.size || defaultState.pagination[type].size
                        }
                    }
                },
                props.definition?.defaults ? { ...props.definition.defaults } : {}, // overlay defaults if provided by the definition
                props?.initialFilters
                    ? {
                          filter: {
                              ...defaultState.filter,
                              ...props.definition?.defaults?.filter,
                              ...props.initialFilters
                          }
                      }
                    : {
                          filter: {
                              ...defaultState.filter,
                              ...props.definition?.defaults?.filter
                          }
                      }, // overlay initialFilters if available
                {
                    columns: props?.definition.columns,
                    additionalFields: props?.definition?.additionalFields || []
                }
            );

            if (props?.definition?.bulkPreselectAll === true) {
                localInitialState.bulkPreselectAll = true;
            }
            if (props?.definition?.bulkEnabled === true) {
                localInitialState.bulkEnabled = true;
            }

            localInitialState.offset = localInitialState.pagination[type]?.offset || defaultState.pagination[type].offset;
            localInitialState.size = localInitialState.pagination[type]?.size || defaultState.pagination[type].size;
            localInitialState.sets = localInitialState.pagination[type]?.sets || defaultState.pagination[type].sets || null;

            dispatch(initState(stateIdentifier, localInitialState));
            dispatch(
                setOption(stateIdentifier, {
                    initialState: localInitialState
                })
            );
        }
    }, [props?.identifier]); // when identity changes, recheck. We don't do this in [] (one time only) as it could produce strange results

    // We can force a filter which is not override-able by the user/overview itself
    useEffect(() => {
        if (props.forceFilter) {
            props.forceFilter.map((o, idx) => {
                dispatch(
                    setFilter(stateIdentifier, {
                        columnIdentifier: o.columnIdentifier,
                        value: o.value
                    })
                );
            });
        }
        return () => {};
    }, [props?.forceFilter]);

    // we reset the size forcefully (when a parent component can not have direct access to the overview)
    useEffect(() => {
        if (props.forceResetSize) {
            setSize(1);
        }
        return () => {};
    }, [props?.forceResetSize]);

    // Add a state to track accumulated results
    const [accumulatedResults, setAccumulatedResults] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const lastOffset = useRef(0);

    // Modify how the overview data is processed
    const overview = props.definition.queryFn(state || {});

    // Effect to handle initial data load and accumulation
    useEffect(() => {
        if (!overview?.data?.results) return;

        // Initial load or filter change
        if (state?.offset === 0) {
            setAccumulatedResults(overview.data.results);
            lastOffset.current = 0;
            setIsLoadingMore(false);
        }

        // Loading more data
        else if (isLoadingMore && state?.offset > lastOffset.current) {
            setAccumulatedResults((prev) => [...prev, ...overview.data.results]);
            lastOffset.current = state.offset;
            setIsLoadingMore(false);
        }
    }, [overview.data]); // Only depend on overview.data

    // Reset when filters change
    useEffect(() => {
        lastOffset.current = 0;
        setAccumulatedResults([]);
        setIsLoadingMore(false);
    }, [state?.filter, state?.ordering]);

    const handleLoadMore = () => {
        if (isLoadingMore) return;
        setIsLoadingMore(true);

        const currentOffset = state.offset || 0;
        const newOffset = currentOffset + state.size;

        dispatch(
            setOption(stateIdentifier, {
                offset: newOffset
            })
        );
    };

    // Function to reset to the initial state (so it also follows initialState info)
    const resetFilters = () => {
        dispatch(setOption(stateIdentifier, state.initialState));
    };

    // Function to load all pages at once (actually sets current resets current set size and does a fetch for 10.000 sets)
    const setLoadAll = () => {
        overview.setSize(1);
        dispatch(
            setOption(stateIdentifier, {
                size: 1,
                loadAll: true
            })
        );
    };

    // Function to mark row(s) as being selected using bulk-selection
    const setBulk = (key) => {
        dispatch(
            setOption(stateIdentifier, {
                bulk: {
                    ...state.bulk,
                    [key]: !state.bulk[key]
                }
            })
        );
    };

    const unSelectInBulk = (key) => {
        dispatch(
            setOption(stateIdentifier, {
                bulk: {
                    ...state.bulk,
                    [key]: false
                }
            })
        );
    };

    const setBulkData = (data) => {
        dispatch(
            setOption(stateIdentifier, {
                bulkData: {
                    ...state.bulkData,
                    ...data
                }
            })
        );
    };

    const setMultiBulk = (list, state = true) => {
        dispatch(
            setOption(stateIdentifier, {
                bulk: {
                    ...state.bulk,
                    ...Object.assign({}, ...list.map((o) => ({ [o]: state })))
                }
            })
        );
    };

    // Method of enabling/disabling the bulk feature
    const toggleBulk = () => {
        dispatch(
            setOption(stateIdentifier, {
                bulkEnabled: !state.bulkEnabled
            })
        );
    };

    // initiate ordering of the result
    const setOrder = (columnIdentifier) => {
        if (columnIdentifier) {
            dispatch(
                setOption(stateIdentifier, {
                    ordering: {
                        columnIdentifier,
                        direction: state.ordering.columnIdentifier === columnIdentifier ? (state.ordering.direction === "-" ? "" : "-") : ""
                    }
                })
            );
        }
    };

    // Set a filter so the results will be filtered
    const setStateFilter = (columnIdentifier, passedValue) => {
        let value = null;

        // provide backwardscompatibility
        if (["string", "boolean"].includes(typeof passedValue) || Array.isArray(passedValue)) {
            value = passedValue;
        } else {
            if (passedValue?.target && passedValue.target?.value) {
                value = passedValue?.target?.value ? passedValue.target.value : "";
            }
        }

        dispatch(
            setOption(stateIdentifier, {
                offset: 0
            })
        );
        dispatch(
            setFilter(stateIdentifier, {
                columnIdentifier,
                value
            })
        );
    };

    // Set the cursor offset
    const setOffset = (offset) => {
        dispatch(
            setOption(stateIdentifier, {
                offset
            })
        );
    };

    // Set the size of the overview in 'pages'. A setSize(3) results in 3 pages being loaded next (if 1 and 2 are already present only 3 is loaded)
    const setSize = (val) => {
        overview.setSize(val); // SWR uses 'size' to determine the pages but this somewhat classes as we use 'size' for determining how many items exist ín a set
        dispatch(
            setOption(stateIdentifier, {
                sets: val
            })
        );
    };

    // run the actual querying function and pass the state to it

    // assign overview
    overviewRef.current = overview;

    // the amount of results in this set
    const count =
        overview.data && !overview.error && overview.data[0] && "count" in overview.data[0] ? overview.data[0].count : overview.data?.count ? overview.data.count : false;

    // the size of the current paginator
    const size = state?.pagination[state.type].size;

    const setsLoaded = overview?.data?.length;
    const setIdx = setsLoaded ? setsLoaded - 1 : false;
    const loadMore = !!overview?.data?.[setIdx]?.next;

    // we create an object which contains information for the "view" portion of the component. We only provide what it needs
    let passProps = {
        ...props,
        overview: {
            ...overview,
            data: props.definition?.pagination?.cursorPaginator?.loadMore
                ? {
                      ...overview.data,
                      results: accumulatedResults,
                      count: overview.data?.count,
                      next: overview.data?.next,
                      previous: overview.data?.previous
                  }
                : overview.data,
            isLoading: isLoadingMore || overview.isValidating
        },
        handleLoadMore,
        loadMoreEnabled: props.definition?.pagination?.cursorPaginator?.loadMore || false,
        hasMoreData: overview?.data?.count > accumulatedResults.length,
        actions: props.definition?.actions || [],
        bulkEnabled: state?.bulkEnabled,
        bulkPreselectAll: state?.bulkPreselectAll || false,
        bulkOptions: props.definition?.bulkOptions || false,
        bulkState: state?.bulk || {},
        bulkData: state?.bulkEnabled ? state?.bulkData || {} : {},
        bulkValue: props.definition?.bulkValue || false,
        columns: props.definition.columns,
        additionalFields: props.definition.additionalFields,
        contextData: props?.contextData || {},
        count: count,
        children: props?.children,
        classNamePrefix: classNamePrefix,
        depricateOldStyling: props?.depricateOldStyling,
        displayFake: state?.displayFake || false,
        displaySummary: state?.displaySummary || false,
        filterState: state?.filter || {},
        filterInitialOpen: props?.definition?.topbar?.some((o) => !!state?.filter?.[o.columnIdentifier]),
        loadMore: loadMore,
        loadAllAllowed: state?.pagination[state.type].loadAllAllowed,
        maxSize: count > 0 ? Math.ceil(count / size) : 1,
        orderingState: state?.ordering || {},
        onClick: props?.onClick || false,
        quickview: props?.quickview,
        search: props.definition?.search,
        size: size,
        stateIdentifier: stateIdentifier,
        sidebar: Array.isArray(props.definition?.sidebar) && props.definition.sidebar.length > 0 ? props.definition.sidebar : false,
        topbar: Array.isArray(props.definition?.topbar) && props.definition.topbar.length > 0 ? props.definition.topbar : false,
        titleRef: titleRef,
        title: props.definition.title,
        RowStructure: props.definition.RowStructure,
        filter: (...args) => {
            setStateFilter(...args);
        },
        loadAll: (opt) => {
            setLoadAll(opt);
        },
        order: (opt) => {
            setOrder(opt);
        },
        refresh: () => overview.mutate(),
        resetFilters: () => resetFilters(),
        setFilter: setStateFilter,
        setLoadAll,
        setOffset,
        setOrder,
        setSize,
        toggleBulk,
        setBulk,
        unSelectInBulk,
        setBulkData,
        setMultiBulk
    };

    // If no data was received (when component is first rendered) create a fake result set which can contain skeleton placeholders
    if (state?.displayFake === true && !overview?.data) {
        const results = [...Array(size).keys()].map((obj, idx) => {
            return { id: idx, fake: true };
        });
        if (["indexPaginator", "cursorPaginator"].includes(state?.type)) {
            passProps.overview = {
                data: {
                    offset: 0,
                    count: size,
                    results: results
                }
            };
        } else {
            passProps.overview = {
                size: 1,
                data: [
                    {
                        results: results
                    }
                ]
            };
        }
    }

    passProps.additions = [];

    // If there was data, but it does not match the default size for our overview, create additional rows for a more uniform experience
    if (["indexPaginator", "cursorPaginator"].includes(state?.type)) {
        if (overview?.data && overview.data.count < passProps.size) {
            passProps.additions = [...Array(passProps.size - (overview.data.count || 0)).keys()];
        }
    } else {
        if (overview?.size === 1 && overview?.data?.[0]?.results.length < size) {
            passProps.additions = [...Array(passProps.size - (overview.data.count || 0)).keys()];
        }
    }

    // It is possible to override the default display of an overview
    // This is useful when we have an off-by-one overview which is diplayed differently
    const OverviewTemplate = props?.definition?.displayOverride || OverviewDisplay;

    // Right now the sidebar is forced into the overview.
    // Todo: This should be abstracted to the OverviewDisplay
    const sidebarActive = Array.isArray(props.definition?.sidebar) && props.definition.sidebar.length > 0;

    if (props?.depricateOldStyling === true) {
        if (sidebarActive) {
            return (
                <>
                    <div className={`${classNamePrefix}-sidebar`}>
                        {props.definition.sidebar.map((item, idx) => {
                            const Component = item.Component;
                            return (
                                <div key={item.title + "-" + idx}>
                                    {/*<h2>{item.title}</h2>*/}
                                    <Component
                                        columnIdentifier={item?.columnIdentifier}
                                        setFilter={passProps.setFilter}
                                        orderingState={passProps.orderingState}
                                        filterState={passProps.filterState}
                                        contextData={passProps.contextData}
                                    />
                                </div>
                            );
                        })}
                    </div>
                    <div className={`${classNamePrefix}-sidebarred-overview`}>
                        <OverviewTemplate {...passProps} />
                    </div>
                </>
            );
        }
        return <OverviewTemplate {...passProps} />;
    }

    return (
        <>
            <div className="row">
                {sidebarActive && (
                    <div className={"col-3 filters"}>
                        {props.definition.sidebar.map((item, idx) => {
                            const Component = item.Component;
                            return (
                                <div key={item.title + "-" + idx}>
                                    {/*<h2>{item.title}</h2>*/}
                                    <Component
                                        columnIdentifier={item?.columnIdentifier}
                                        setFilter={passProps.setFilter}
                                        orderingState={passProps.orderingState}
                                        filterState={passProps.filterState}
                                        contextData={passProps.contextData}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={sidebarActive ? "col-9" : "col-12"}>
                    <OverviewTemplate {...passProps} />
                </div>
            </div>
        </>
    );
}

// forwardRef must be used because functional components do not support refs. We use the forwarded ref to expose the mutate function to a parent
export default Overview = forwardRef(Overview);
